import { format as monthUtilFormat } from 'loot-core/src/shared/months';

import { useDateFormat } from '../../hooks/useDateFormat';
import { SvgCheck, SvgRemove } from '../../icons/v2';
import { useResponsive } from '../../ResponsiveProvider';
import { styles, theme } from '../../style';
import { Card } from '../common/Card';
import { Text } from '../common/Text';
import { Field, Row, Table, TableHeader } from '../table';
import { Button } from '../common/Button';
import React from 'react';
import { send } from 'loot-core/platform/client/fetch';
import { useDispatch } from 'react-redux';
import { pushModal, updateLinkedAccounts } from 'loot-core/client/actions';

const ROW_HEIGHT = 43;

type IFinancialInstitution = {
  id: string;
  name: string;
  status: string;
  provider: string;
  accountsCount: number;
  connectedAt: string;
  connectionId: string;
  institutionId: string;
  logoUrl: string;
  isOauth: boolean;
};

type IDashboardFinancialInstitutionsProps = {
  financialInstitutions: IFinancialInstitution[];
  onReloadRequest: () => void;
};

const BillingFinancialInstitutions: React.FC<
  IDashboardFinancialInstitutionsProps
> = ({ financialInstitutions, onReloadRequest }) => {
  const dateFormat = useDateFormat() || 'MM/dd/yyyy';
  const dispatch = useDispatch();
  const { width, isWideWidth } = useResponsive();

  const getStatusComponent = (status: string) => {
    const isError = status.startsWith('ERROR');
    return (
      <div
        style={{
          display: 'flex',
          width: '8px',
          height: '8px',
          borderRadius: '4px',
          alignSelf: 'center',
          backgroundColor: isError
            ? theme.errorText
            : theme.sidebarItemBackgroundPositive,
        }}
      />
    );
  };

  const onUnlink = async (connectionId: string) => {
    dispatch(
      pushModal('confirm-generic', {
        title: 'Unlinking Financial Institution',
        text: `Unlinking this financial institution will:

          <ul>
            <li>Disconnect all linked bank accounts (they’ll show a red dot until reconnected).</li>
            <li>Affect your current budget file and any other budgets using this connection.</li>
          </ul>

          You can always reconnect later, but this change will take effect immediately and cannot be undone.`,
        confirmBtnText: 'Unlink',
        onConfirm: async () => {
          await send('connection-unlink', { connectionId });
          dispatch(updateLinkedAccounts());
          onReloadRequest();
        },
      }),
    );
  };

  const columnWidths = {
    name: 'flex',
    provider: 'flex',
    date: !isWideWidth ? 86 : 110,
    numberOfAccounts: !isWideWidth ? 65 : 110,
    status: 70,
    oauth: 70,
  };

  function renderFinInstitution({
    item: financialInstitution,
  }: {
    item: IFinancialInstitution;
  }) {
    return (
      <Row
        height={ROW_HEIGHT}
        inset={0}
        style={{
          backgroundColor: theme.tableBackground,
          color: theme.tableText,
          ':hover': { backgroundColor: theme.tableRowBackgroundHover },
        }}
      >
        <Field width={columnWidths['name']} name="name">
          <Text style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {/*<img*/}
            {/*  style={{ verticalAlign: 'middle' }}*/}
            {/*  width={columnWidths[]}*/}
            {/*  src={financialInstitution.logoUrl}*/}
            {/*/>*/}
            <span
              style={{
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
              }}
            >
              {financialInstitution.name}
            </span>
          </Text>
        </Field>
        <Field width={columnWidths['provider']} name="provider">
          <Text>{financialInstitution.provider}</Text>
        </Field>
        <Field width={columnWidths['date']} name="date">
          {financialInstitution.connectedAt
            ? monthUtilFormat(
                new Date(financialInstitution.connectedAt),
                dateFormat,
              )
            : 'N/A'}
        </Field>
        <Field
          width={columnWidths['status']}
          name="status"
          style={{ alignItems: 'center' }}
        >
          {getStatusComponent(financialInstitution.status)}
        </Field>
        <Field
          width={columnWidths['oauth']}
          name="oauth"
          style={{ alignItems: 'center' }}
        >
          {financialInstitution.isOauth ? (
            <SvgCheck
              width={12}
              height={12}
              style={{ color: theme.gsGreen700 }}
            />
          ) : (
            <SvgRemove
              width={10}
              height={10}
              style={{ color: theme.errorBorder }}
            />
          )}
        </Field>
        <Field
          width={columnWidths['oauth']}
          name="oauth"
          style={{ alignItems: 'center' }}
        >
          <Button
            type={'bare'}
            disabled={financialInstitution.status === 'DISCONNECTING'}
            onClick={() => onUnlink(financialInstitution.connectionId)}
          >
            {financialInstitution.status === 'DISCONNECTING'
              ? 'Unlinking'
              : 'Unlink'}
          </Button>
        </Field>
      </Row>
    );
  }

  return (
    <Card
      style={{
        padding: '0 16px',
        margin: 0,
        maxHeight: 11 * ROW_HEIGHT,
      }}
    >
      <p style={{ ...styles.mediumText, fontWeight: 700 }}>
        Linked financial institutions
      </p>
      {financialInstitutions.length ? (
        <>
          <TableHeader height={ROW_HEIGHT}>
            <Field width={columnWidths['name']}>Name</Field>
            <Field width={columnWidths['provider']}>Provider</Field>
            <Field width={columnWidths['date']}>Date connected</Field>
            <Field
              width={columnWidths['status']}
              style={{ alignItems: 'center' }}
            >
              Status
            </Field>
            <Field
              width={columnWidths['oauth']}
              style={{ alignItems: 'center' }}
            >
              OAuth
            </Field>
            <Field
              width={columnWidths['oauth']}
              style={{ alignItems: 'center' }}
            >
              Actions
            </Field>
          </TableHeader>
          <Table
            rowHeight={ROW_HEIGHT}
            backgroundColor="transparent"
            style={{ flex: 1, backgroundColor: 'transparent' }}
            items={financialInstitutions as any[]}
            renderItem={renderFinInstitution}
            allowPopupsEscape={financialInstitutions.length < 6}
          />
        </>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '78px',
          }}
        >
          You haven't linked any financial institutions yet
        </div>
      )}
    </Card>
  );
};
export default BillingFinancialInstitutions;
